import React, { useState } from 'react';

interface TimeSlot {
  value: string;
  label: string;
  disabled?: boolean;
}

interface TimeSlotSelectorProps {
  selectedTime?: string;
  onTimeSelect?: (time: string) => void;
  selectedDuration?: string;
  onDurationSelect?: (duration: string) => void;
  selectedFrequency?: string;
  onFrequencySelect?: (frequency: string) => void;
  className?: string;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  selectedTime,
  onTimeSelect,
  selectedDuration,
  onDurationSelect,
  selectedFrequency,
  onFrequencySelect,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const timeSlots: { morning: TimeSlot[]; midday: TimeSlot[]; evening: TimeSlot[] } = {
    morning: [
      { value: '06:00', label: '6:00am' },
      { value: '06:30', label: '6:30am' },
      { value: '07:00', label: '7:00am' },
      { value: '07:30', label: '7:30am' },
      { value: '08:00', label: '8:00am' },
      { value: '08:30', label: '8:30am' },
      { value: '09:00', label: '9:00am' },
      { value: '09:30', label: '9:30am' },
      { value: '10:00', label: '10:00am' },
      { value: '10:30', label: '10:30am' },
      { value: '11:00', label: '11:00am' },
      { value: '11:30', label: '11:30am' }
    ],
    midday: [
      { value: '12:00', label: '12:00pm' },
      { value: '12:30', label: '12:30pm' },
      { value: '13:00', label: '1:00pm' },
      { value: '13:30', label: '1:30pm' },
      { value: '14:00', label: '2:00pm' },
      { value: '14:30', label: '2:30pm' },
      { value: '15:00', label: '3:00pm' },
      { value: '15:30', label: '3:30pm' },
      { value: '16:00', label: '4:00pm' },
      { value: '16:30', label: '4:30pm' },
      { value: '17:00', label: '5:00pm' },
      { value: '17:30', label: '5:30pm' }
    ],
    evening: [
      { value: '18:00', label: '6:00pm' },
      { value: '18:30', label: '6:30pm' },
      { value: '19:00', label: '7:00pm' },
      { value: '19:30', label: '7:30pm' },
      { value: '20:00', label: '8:00pm' },
      { value: '20:30', label: '8:30pm' },
      { value: '21:00', label: '9:00pm' },
      { value: '21:30', label: '9:30pm' },
      { value: '22:00', label: '10:00pm' },
      { value: '22:30', label: '10:30pm' },
      { value: '23:00', label: '11:00pm' },
      { value: '23:30', label: '11:30pm' }
    ]
  };


  const tabs: { label: string; slots: TimeSlot[] }[] = [
    { label: 'Morning Slots', slots: timeSlots.morning },
    { label: 'Midday Slots', slots: timeSlots.midday },
    { label: 'Evening Slots', slots: timeSlots.evening }
  ];

  const handleTimeSelect = (time: string) => {
    if (onTimeSelect) {
      onTimeSelect(time);
    }
  };

  const handleDurationSelect = (duration: string) => {
    if (onDurationSelect) {
      onDurationSelect(duration);
    }
  };

  const handleFrequencySelect = (frequency: string) => {
    if (onFrequencySelect) {
      onFrequencySelect(frequency);
    }
  };

  return (
    <>
      <div className={`space-y-2 ${className}`}>
        <p className="font-bold">Start Time</p>

        <div className="start-time-tabs">
          <div className="tabs-header" role="tablist">
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`tab-trigger group ${activeTab === index ? 'active' : ''
                  }`}
                role="tab"
                aria-selected={activeTab === index}
                aria-controls={`panel-${index}`}
                id={`tab-${index}`}
                tabIndex={activeTab === index ? 0 : -1}
                onClick={() => setActiveTab(index)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tabs-content mt-5">
            {tabs.map((tab, tabIndex) => (
              <div
                key={tabIndex}
                id={`panel-${tabIndex}`}
                className={`tab-content ${activeTab === tabIndex ? 'animate-fadeIn' : 'hidden'
                  }`}
                role="tabpanel"
                aria-labelledby={`tab-${tabIndex}`}
                {...(activeTab !== tabIndex && { 'aria-hidden': 'true' })}
              >
                <div className="grid max-w-[476px] grid-cols-[repeat(4,minmax(0,1fr))] gap-4">
                  {tab.slots.map((slot, index) => (
                    <label key={`${tabIndex}-${index}`} className="flex cursor-pointer">
                      <input
                        type="radio"
                        name="available_time"
                        value={slot.value}
                        className="peer hidden"
                        disabled={slot.disabled}
                        checked={selectedTime === slot.value}
                        onChange={() => handleTimeSelect(slot.value)}
                      />
                      <span className="checkbox-group-item">
                        {slot.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button className="btn btn-secondary mt-4">
          <span>Enter Your Start Time</span>
        </button>
      </div>


      {/* Duration */}
      <div className="space-y-2">
        <p className="font-bold">Duration</p>

        <div className="grid max-w-[476px] grid-cols-[repeat(4,minmax(0,1fr))] gap-4">
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="15"
              className="peer hidden"
              checked={selectedDuration === '15'}
              onChange={() => handleDurationSelect('15')}
            />
            <span className="checkbox-group-item py-0">15 mins</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="30"
              className="peer hidden"
              checked={selectedDuration === '30'}
              onChange={() => handleDurationSelect('30')}
            />
            <span className="checkbox-group-item py-0">30 mins</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="45"
              className="peer hidden"
              checked={selectedDuration === '45'}
              onChange={() => handleDurationSelect('45')}
            />
            <span className="checkbox-group-item py-0">45 mins</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="60"
              className="peer hidden"
              checked={selectedDuration === '60'}
              onChange={() => handleDurationSelect('60')}
            />
            <span className="checkbox-group-item py-0">1 hr</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="90"
              className="peer hidden"
              checked={selectedDuration === '90'}
              onChange={() => handleDurationSelect('90')}
            />
            <span className="checkbox-group-item py-0">1 hr 30 mins</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="120"
              className="peer hidden"
              checked={selectedDuration === '120'}
              onChange={() => handleDurationSelect('120')}
            />
            <span className="checkbox-group-item py-0">2 hrs</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="150"
              className="peer hidden"
              checked={selectedDuration === '150'}
              onChange={() => handleDurationSelect('150')}
            />
            <span className="checkbox-group-item py-0">2 hr 30 mins</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="duration"
              value="180"
              className="peer hidden"
              checked={selectedDuration === '180'}
              onChange={() => handleDurationSelect('180')}
            />
            <span className="checkbox-group-item py-0">3 hrs</span>
          </label>
        </div>

        <button className="btn btn-secondary mt-4" disabled>
          <span>Enter Your Duration</span>
        </button>
      </div>


      {/* Session Frequency */}
      <div className="space-y-2">
        <p className="font-bold">Session Frequency</p>

        <div className="flex max-w-[476px] flex-wrap gap-4">
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="frequency"
              value="none"
              className="peer hidden"
              checked={selectedFrequency === 'none'}
              onChange={() => handleFrequencySelect('none')}
            />
            <span className="checkbox-group-item py-0">Does not Repeat</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="frequency"
              value="daily"
              className="peer hidden"
              checked={selectedFrequency === 'daily'}
              onChange={() => handleFrequencySelect('daily')}
              disabled
            />
            <span className="checkbox-group-item py-0">Daily</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="frequency"
              value="weekday"
              className="peer hidden"
              checked={selectedFrequency === 'weekday'}
              onChange={() => handleFrequencySelect('weekday')}
              disabled
            />
            <span className="checkbox-group-item py-0">Every weekday</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="frequency"
              value="weekly"
              className="peer hidden"
              checked={selectedFrequency === 'weekly'}
              onChange={() => handleFrequencySelect('weekly')}
              disabled
            />
            <span className="checkbox-group-item py-0">Weekly</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="frequency"
              value="monthly"
              className="peer hidden"
              checked={selectedFrequency === 'monthly'}
              onChange={() => handleFrequencySelect('monthly')}
              disabled
            />
            <span className="checkbox-group-item py-0">Monthly</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="frequency"
              value="custom"
              className="peer hidden"
              checked={selectedFrequency === 'custom'}
              onChange={() => handleFrequencySelect('custom')}
              disabled
            />
            <span className="checkbox-group-item py-0">Every three days, until August 23, 2025</span>
          </label>
        </div>

        <button className="btn btn-secondary mt-4" disabled>
          <span>Custom Session Frequency</span>
        </button>
      </div>
    </>
  );
};

export default TimeSlotSelector;